import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useAddSaleLot } from '../hooks/use-sale-mutations';
import { useLooseDraftAuctions } from '../hooks/use-sales';

import { LotThumb } from './lot-thumb';

/** Draft auctions in no sale yet; one is chosen and joins the sale as its last lot. */
export function AddSaleLotDialog({
  storeId,
  saleId,
  open,
  onOpenChange,
}: {
  storeId: string;
  saleId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['auctions', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const drafts = useLooseDraftAuctions(storeId, open);
  const add = useAddSaleLot(storeId, saleId);
  const [chosen, setChosen] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    onOpenChange(false);
    setChosen('');
    setError(null);
    add.reset();
  };

  const confirm = async () => {
    if (!chosen) return;
    try {
      await add.mutateAsync(chosen);
      close();
    } catch (err) {
      setError(message(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('auctions:sales.addLot.title')}</DialogTitle>
          <DialogDescription>{t('auctions:sales.addLot.description')}</DialogDescription>
        </DialogHeader>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {drafts.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : drafts.error ? (
          <ErrorState error={drafts.error} onRetry={() => void drafts.refetch()} />
        ) : drafts.data.length > 0 ? (
          <RadioGroup
            value={chosen}
            onValueChange={setChosen}
            className="max-h-80 gap-1 overflow-y-auto"
            aria-label={t('auctions:sales.addLot.listLabel')}
          >
            {drafts.data.map((auction) => (
              <Label
                key={auction.id}
                htmlFor={`add-lot-${auction.id}`}
                className="hover:bg-muted/60 flex cursor-pointer items-center gap-3 rounded-md border p-2"
              >
                <RadioGroupItem value={auction.id} id={`add-lot-${auction.id}`} />
                <LotThumb lot={auction.lot} size={32} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">{auction.lot.title}</span>
                  <span className="text-muted-foreground truncate text-xs font-normal">
                    {t('auctions:summary.startingAt', {
                      price: format.money(auction.startingPrice),
                    })}
                  </span>
                </span>
              </Label>
            ))}
          </RadioGroup>
        ) : (
          <p className="text-muted-foreground text-sm">{t('auctions:sales.addLot.none')}</p>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" disabled={!chosen || add.isPending} onClick={() => void confirm()}>
            {add.isPending ? t('auctions:actions.working') : t('auctions:sales.addLot.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
